import React, { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import { EventContext, OfferStoreContext } from "@gp/components";
import { LiveStatus, LiveStreamStatus } from "@gp/models";
import { EventOffer } from "@gp/offer";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import { analyticsTracker } from "@utils/analytics";
import classNames from "classnames";
import { observer } from "mobx-react";

export default observer(function LiveStreamToggleButton(props: {
	isOnEventView?: boolean;
	/** Will widget button control side widget */
	isForSideWidget?: boolean;
	/** Is button on event or on event row */
	isOfferButton?: boolean;
	/** Should button only open widget, not close if already opened. */
	isOnlyOpen?: boolean;
}) {
	const [isHover, setIsHover] = useState(false);

	const liveWidgetStore = useLiveWidgetStore();
	const event = useContext(EventContext) as EventOffer | undefined;
	const { t } = useTranslation();
	const store = useContext(OfferStoreContext);

	const teamOne = store.lookupsStore.teams.get(event?.teamOneId || "");
	const teamTwo = store.lookupsStore.teams.get(event?.teamTwoId || "");

	const tournament = store.lookupsStore.tournaments.get(
		event?.tournamentId || ""
	);

	const sport = store.lookupsStore.sports.get(event?.sportId || "");

	const location: WidgetLocation = props.isForSideWidget
		? WidgetLocation.SIDE
		: WidgetLocation.EVENT_OFFER;

	const isUpcoming = event?.liveStreamStatus === LiveStreamStatus.Upcoming;
	const isFinished = event?.liveStreamStatus === LiveStreamStatus.Finished;
	const eventHasLsError = liveWidgetStore.eventsWithLsError.has(
		event?.id || ""
	);

	const isDisabled =
		event?.liveStreamStatus == null ||
		isUpcoming ||
		isFinished ||
		eventHasLsError;

	let dataTip = t("LIVE_WIDGETS.LIVE_STREAM.STREAM_UNAVAILABLE");

	if (!eventHasLsError) {
		if (event?.liveStreamStatus === LiveStreamStatus.Upcoming) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.UPCOMING");
		} else if (event?.liveStreamStatus === LiveStreamStatus.Live) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.LIVE");
		} else if (event?.liveStreamStatus === LiveStreamStatus.Finished) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.CANCELLED");
		}
	}

	const isActive = liveWidgetStore.findActiveLiveStreamWidget(
		event?.id || "",
		location
	);

	const buttonClasses = classNames(
		"at-actionLiveStream btn",
		{
			offer__actions__btn: props.isOfferButton,
			"widget__actions__btn widget__actions__video":
				!props.isOfferButton && props.isForSideWidget,
		},
		{
			"btn--square--reset btn--primary--inverted": props.isOfferButton,
			"btn--square btn--square--sml btn--ghost":
				(!props.isOfferButton && props.isForSideWidget) ||
				props.isOnEventView,
			"is-disabled": isDisabled,
		}
	);

	const iconClasses = classNames(
		{
			offer__actions__icon: props.isOfferButton && !props.isForSideWidget,
		},
		"u-icon",
		{
			"u-icon--sml": props.isOfferButton,
			"u-icon--med": !props.isOfferButton,
			"u-icon--action--stream--active":
				!isHover && !isActive && !isDisabled,
			"u-icon--action--stream--selected":
				(isHover || isActive) && !isDisabled,
			"u-icon--action--stream--disabled": isDisabled,
			"u-icon--action--stream--cancelled": isFinished,
			"u-icon--action--stream--upcoming": isUpcoming,
		}
	);

	const handleOnMouseEnter: React.MouseEventHandler<
		HTMLButtonElement
	> = (): void => {
		setIsHover(true);
	};

	const handleOnMouseLeave: React.MouseEventHandler<
		HTMLButtonElement
	> = (): void => {
		setIsHover(false);
	};

	if (props.isOnEventView && isDisabled) {
		return null;
	}

	return (
		<button
			className={buttonClasses}
			type="button"
			data-tooltip-id="tv-description"
			data-tooltip-content={dataTip}
			data-tooltip-position-strategy="fixed"
			disabled={isDisabled}
			onMouseEnter={handleOnMouseEnter}
			onMouseLeave={handleOnMouseLeave}
			onClick={() => {
				if (!event?.id) {
					return;
				}
				if (!isActive) {
					analyticsTracker.trackLiveStreamingViewed(
						event?.id || "",
						teamOne?.abrv || "",
						teamTwo?.abrv || "",
						tournament?.name || "",
						sport?.abrv || ""
					);
				}

				const config = {
					eventId: event.id,
					teamOneName: teamOne?.name || "",
					teamTwoName: teamTwo?.name || "",
					location,
					isUserInitiated: true,
					autoPlay: true,
				};

				if (props.isOnlyOpen) {
					liveWidgetStore.addLiveStreamWidget(config);
				} else {
					liveWidgetStore.toggleLsWidget(config);
				}
			}}
		>
			<i className={iconClasses} />
		</button>
	);
});
